import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FileSignature, Clock, ArrowRight, CheckCircle2, Building2, MoreVertical, EyeOff, Clock3, CalendarPlus, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GIORNI_KEY = 'promemoria_conti_giorni';
const GIORNI_OPZIONI = [7, 10, 15, 20, 30, 45, 60, 90];

/**
 * iter101 — Widget "Promemoria Conti"
 * Mostra i cantieri attivi per cui NON è stato ancora emesso un conto e
 * il cui ultimo rapportino risale a più di `giorni` giorni fa.
 * Ogni voce ha un link diretto per creare il conto nel cantiere.
 * iter102 — soglia giorni configurabile dalla dashboard (persistita in localStorage).
 */
export default function PromemoriaContiWidget() {
  const [items, setItems] = useState([]);
  const [giorni, setGiorni] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(GIORNI_KEY), 10);
      return GIORNI_OPZIONI.includes(v) ? v : 20;
    } catch { return 20; }
  });
  const [loading, setLoading] = useState(true);

  const changeGiorni = (v) => {
    const n = parseInt(v, 10);
    setGiorni(n);
    try { localStorage.setItem(GIORNI_KEY, String(n)); } catch { /* ignore */ }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/promemoria-conti`, {
        params: { giorni },
        withCredentials: true,
      });
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [giorni]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pad = (n) => String(n).padStart(2, '0');
  const [rimandaFor, setRimandaFor] = useState(null); // cantiere object
  const [rimandaGiorni, setRimandaGiorni] = useState(7);
  const [calFor, setCalFor] = useState(null); // cantiere object
  const [calForm, setCalForm] = useState({ date: '', start: '09:00', end: '10:00' });

  const doEscludi = async (c) => {
    try {
      await axios.post(`${API}/dashboard/promemoria-conti/${c.cantiere_id}/escludi`, {}, { withCredentials: true });
      toast.success(`"${c.cantiere_nome}" escluso dai promemoria`);
      fetchData();
    } catch { toast.error('Errore'); }
  };
  const doContoInviato = async (c) => {
    try {
      await axios.post(`${API}/dashboard/promemoria-conti/${c.cantiere_id}/conto-inviato`, {}, { withCredentials: true });
      toast.success(`Conto segnato come inviato. Riapparirà solo con nuovi rapportini.`);
      fetchData();
    } catch { toast.error('Errore'); }
  };
  const openRimanda = (c) => { setRimandaGiorni(7); setRimandaFor(c); };
  const confirmRimanda = async () => {
    try {
      await axios.post(`${API}/dashboard/promemoria-conti/${rimandaFor.cantiere_id}/rimanda`, { giorni: Number(rimandaGiorni) || 7 }, { withCredentials: true });
      toast.success(`Promemoria rimandato di ${rimandaGiorni} giorni`);
      setRimandaFor(null);
      fetchData();
    } catch { toast.error('Errore'); }
  };
  const openCal = (c) => {
    const d = new Date();
    setCalForm({ date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, start: '09:00', end: '10:00' });
    setCalFor(c);
  };
  const confirmCal = async () => {
    try {
      await axios.post(`${API}/dashboard/calendar-events`, {
        summary: `Emettere conto — ${calFor.cantiere_nome}`,
        dtstart: `${calForm.date}T${calForm.start}:00`,
        dtend: `${calForm.date}T${calForm.end}:00`,
        colore: 'amber',
        description: calFor.cliente_nome ? `Cliente: ${calFor.cliente_nome}` : '',
      }, { withCredentials: true });
      toast.success('Evento aggiunto al calendario');
      setCalFor(null);
    } catch { toast.error('Errore'); }
  };

  return (
    <Card className="border-amber-200" data-testid="promemoria-conti-widget">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-amber-600" />
            Promemoria Conti
            {items.length > 0 && (
              <Badge className="bg-amber-500 text-white ml-1" data-testid="promemoria-conti-count">{items.length}</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-1.5 shrink-0" data-testid="promemoria-giorni-selector">
            <span className="text-[11px] text-slate-500 whitespace-nowrap">oltre</span>
            <Select value={String(giorni)} onValueChange={changeGiorni}>
              <SelectTrigger className="h-7 w-[64px] px-2 text-xs" data-testid="promemoria-giorni-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GIORNI_OPZIONI.map((g) => (
                  <SelectItem key={g} value={String(g)} data-testid={`promemoria-giorni-${g}`}>{g} gg</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          Cantieri attivi senza conto emesso e con ultimo rapportino da oltre {giorni} giorni
        </p>
      </CardHeader>
      <CardContent className="pt-1">
        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 flex flex-col items-center text-center gap-1.5 text-slate-500" data-testid="promemoria-conti-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <p className="text-sm font-medium text-slate-700">Tutto in regola</p>
            <p className="text-xs">Nessun cantiere richiede l&apos;emissione di un conto al momento.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
            {items.map((c) => (
              <div
                key={c.cantiere_id}
                className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-amber-100 bg-amber-50/40 hover:bg-amber-50 transition-colors"
                data-testid={`promemoria-conto-row-${c.cantiere_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.cantiere_nome}</p>
                  </div>
                  {c.cliente_nome && (
                    <p className="text-xs text-slate-500 truncate ml-5">{c.cliente_nome}</p>
                  )}
                  <div className="flex items-center gap-1 ml-5 mt-0.5">
                    <Clock className="h-3 w-3 text-amber-600" />
                    <span className="text-[11px] text-amber-700 font-medium">
                      Ultimo rapportino {c.giorni_fa} giorni fa
                    </span>
                    <span className="text-[11px] text-slate-400">· {c.num_rapportini} rapportini</span>
                  </div>
                </div>
                <a
                  href={`/admin/cantieri?tab=contabilita&cantiere=${c.cantiere_id}&crea_conto=1`}
                  data-testid={`promemoria-crea-conto-${c.cantiere_id}`}
                >
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap">
                    Crea conto <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </a>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className="px-1.5" data-testid={`promemoria-azioni-${c.cantiere_id}`}>
                      <MoreVertical className="h-4 w-4 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={() => openCal(c)} data-testid={`promemoria-calendario-${c.cantiere_id}`}>
                      <CalendarPlus className="h-4 w-4 mr-2 text-blue-600" /> Aggiungi al calendario
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openRimanda(c)} data-testid={`promemoria-rimanda-${c.cantiere_id}`}>
                      <Clock3 className="h-4 w-4 mr-2 text-amber-600" /> Rimanda…
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => doContoInviato(c)} data-testid={`promemoria-conto-inviato-${c.cantiere_id}`}>
                      <Send className="h-4 w-4 mr-2 text-emerald-600" /> Conto già inviato
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => doEscludi(c)} className="text-red-600" data-testid={`promemoria-escludi-${c.cantiere_id}`}>
                      <EyeOff className="h-4 w-4 mr-2" /> Escludi dal promemoria
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog Rimanda */}
      <Dialog open={!!rimandaFor} onOpenChange={(o) => { if (!o) setRimandaFor(null); }}>
        <DialogContent className="max-w-xs" data-testid="promemoria-rimanda-dialog">
          <DialogHeader>
            <DialogTitle>Rimanda promemoria</DialogTitle>
            <DialogDescription>Di quanti giorni vuoi posticipare il promemoria per &quot;{rimandaFor?.cantiere_nome}&quot;?</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input type="number" min="1" max="365" value={rimandaGiorni} onChange={(e) => setRimandaGiorni(e.target.value)} className="w-24" data-testid="promemoria-rimanda-giorni" />
            <span className="text-sm text-slate-600">giorni</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRimandaFor(null)}>Annulla</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={confirmRimanda} data-testid="promemoria-rimanda-conferma">Rimanda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiungi al calendario */}
      <Dialog open={!!calFor} onOpenChange={(o) => { if (!o) setCalFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="promemoria-calendario-dialog">
          <DialogHeader>
            <DialogTitle>Aggiungi al calendario</DialogTitle>
            <DialogDescription>Crea un evento &quot;Emettere conto&quot; per &quot;{calFor?.cantiere_nome}&quot;.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Data</label>
              <Input type="date" value={calForm.date} onChange={(e) => setCalForm({ ...calForm, date: e.target.value })} data-testid="promemoria-cal-date" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500">Inizio</label>
                <Input type="time" value={calForm.start} onChange={(e) => setCalForm({ ...calForm, start: e.target.value })} data-testid="promemoria-cal-start" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Fine</label>
                <Input type="time" value={calForm.end} onChange={(e) => setCalForm({ ...calForm, end: e.target.value })} data-testid="promemoria-cal-end" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalFor(null)}>Annulla</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={confirmCal} data-testid="promemoria-cal-conferma">Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
